// components/Header.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown, Space } from "antd";
import {
  UserOutlined,
  LogoutOutlined,
  IdcardOutlined,
  DownOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import multiavatar from "@multiavatar/multiavatar";
import { useAuth } from "../context/AuthContext";
import { isAdminRole, normalizeRole } from "../utils/roles.utils";
import { getModal, getMsg } from "../utils/antdStatic";
import { limpiarFiltrosGuardados } from "../hooks/filtrosPersistidos";
import AlertsBell from "./AlertsBell";
import NotificacionesBell from "./NotificacionesBell";
import "./Header.css";

/**
 * Borra los filtros que el sistema recuerda de este usuario y recarga.
 *
 * Es la salida de emergencia del round 2026-09-24: desde ese round las
 * búsquedas, selecciones y rangos del admin se recuerdan entre sesiones, y un
 * filtro viejo puede dejar una tabla vacía sin que se vea el motivo. Esto
 * devuelve todo a los valores por defecto sin tocar ningún dato.
 *
 * `recargar` se inyecta para poder probarlo sin tocar window.location.
 */
export function confirmarLimpiarFiltros(
  idUsuario?: number,
  recargar: () => void = () => window.location.reload(),
): void {
  const limpiar = () => {
    const cuantos = limpiarFiltrosGuardados(idUsuario);
    getMsg()?.success(
      cuantos > 0
        ? `Se limpiaron ${cuantos} filtros guardados`
        : "No había filtros guardados",
    );
    recargar();
  };
  const modal = getModal();
  if (!modal) {
    limpiar();
    return;
  }
  modal.confirm({
    title: "¿Limpiar los filtros guardados?",
    content:
      "Las búsquedas, selecciones, rangos de fecha y pestañas que el sistema recuerda en este equipo vuelven a sus valores por defecto. No se modifica ningún dato.",
    okText: "Limpiar",
    cancelText: "Cancelar",
    onOk: limpiar,
  });
}

interface HeaderProps {
  toggleSidebar: () => void;
}

// Round 2026-04-30 octies — re-diseño del header.
// Antes: dos botones <button> con emojis 🔔 (campana sin onClick, dead
// code) y 👤 (lleva a /perfil). La campana no servía para nada y el
// avatar no mostraba al usuario logueado.
// Ahora: campana removida hasta que tengamos sistema real de
// notificaciones (los eventos viven en WebSocket, podemos conectar más
// adelante). Avatar pasa a ser un Dropdown AntD + menu "Mi perfil" +
// "Cerrar sesión".
//
// Round 2026-07-13 — el avatar del header ahora refleja el MISMO avatar
// que eligió el usuario en /perfil (foto subida o Multiavatar de su
// semilla), igual que el SideBar. Antes usaba `multiavatar(nombre)` fijo,
// así que no cambiaba al actualizar el avatar. `usePerfilLogic.persistAvatar`
// sincroniza el AuthContext (`updateUser`) para que cambie en vivo.
const Header: React.FC<HeaderProps> = ({ toggleSidebar: _toggleSidebar }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const nombre = user?.nombre || user?.email || "Usuario";
  const esAdmin = isAdminRole(user?.rol);
  // Campana de avisos personales (aseos nuevos que entran a su ventana). Distinta de la de
  // alertas del admin. Round 2026-10-28.
  const esAseadora = normalizeRole(user?.rol) === "aseador";

  const items = [
    {
      key: "perfil",
      icon: <IdcardOutlined />,
      label: "Mi perfil",
      onClick: () => navigate("/perfil"),
    },
    // Solo admin/superadmin: son los únicos a quienes se les recuerdan los
    // filtros, así que a nadie más le serviría el botón.
    ...(esAdmin
      ? [
          {
            key: "limpiar-filtros",
            icon: <ClearOutlined />,
            label: "Limpiar filtros guardados",
            onClick: () => confirmarLimpiarFiltros(user?.id),
          },
        ]
      : []),
    {
      type: "divider" as const,
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Cerrar sesión",
      danger: true,
      onClick: () => {
        logout();
        navigate("/login");
      },
    },
  ];

  return (
    <header className="home-header">
      <div className="logo-container" style={{ marginLeft: "50px" }}>
        <h1 onClick={() => navigate("/home")} style={{ cursor: "pointer" }}>
          Aloja Fácil
        </h1>
      </div>
      <div className="header-actions">
        {esAdmin && <AlertsBell />}
        {esAseadora && <NotificacionesBell />}
        <Dropdown
          menu={{ items }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Space
            size={6}
            style={{ cursor: "pointer", padding: "4px 8px" }}
            align="center"
          >
            {/* No usamos <Avatar> de AntD: cuando recibe `icon` Y `children`,
                el `icon` gana y el multiavatar (children) nunca se muestra —
                por eso el header mostraba siempre el ícono de persona. Contenedor
                propio de 32px con el SVG dimensionado por CSS (patrón del hero). */}
            <span className="header-avatar">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="Avatar" />
              ) : user?.avatar || nombre ? (
                <span
                  className="header-avatar-svg"
                  dangerouslySetInnerHTML={{
                    __html: multiavatar(user?.avatar || nombre),
                  }}
                />
              ) : (
                <UserOutlined />
              )}
            </span>
            <span className="header-user-name">{nombre}</span>
            <DownOutlined style={{ fontSize: 10, opacity: 0.6 }} />
          </Space>
        </Dropdown>
      </div>
    </header>
  );
};

export default Header;
